'use client';

/**
 * Settings ▸ Flags — every language the product speaks, with its flags as
 * switches.
 *
 * ── What this replaced ──────────────────────────────────────────────────────
 * One line in the backend's language registry. Korea's flag was not on this
 * site because the Korean entry shipped with `countries: []`, and the Registers
 * button special-cased `ko` to draw the site's mark instead. That is an opinion
 * about flags baked into a data file and a component, held by the codebase on
 * behalf of everybody using it, and changeable only by a developer and a
 * deploy. It is now a per-account setting: the registry states the fact (Korean
 * is spoken in Korea, whose flag is this one), and each person decides what
 * they want drawn.
 *
 * ── Why the unit is a COUNTRY and not a language ────────────────────────────
 * Because a flag is a country, and the same one turns up under several
 * languages — `es` appears under Spanish, Basque, Catalan and Galician, `in`
 * under a dozen. Switching one off per language would mean four separate
 * answers to one question, and somebody who does not want a flag on their
 * screen would find it still there three rows further down. So the switch is
 * the flag itself, wherever it appears, and the panel says so in as many words.
 *
 * ── Why the list is of LANGUAGES anyway ─────────────────────────────────────
 * Because a wall of 144 flags is not something anybody can find Korea in. The
 * language is the handle people have on this ("I do not want the Korean flag"),
 * so the language is what the list is grouped and searched by, even though the
 * thing being toggled is the country.
 *
 * Nothing here is saved with a button. Each switch writes on the spot, which is
 * what makes the list feel like the product changing rather than a form being
 * filled in — and the switch beneath your finger IS the confirmation, because
 * every other copy of that flag on the page changes with it.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchLanguages, type Language } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import FlagIcon, { hasFlag } from './FlagIcon';
import { S_BTN, S_INPUT } from './settingsUi';

/**
 * The registry, fetched once per page rather than once per opening.
 *
 * `/api/languages` is static and gzips to a few KB, so this is not about the
 * bytes — it is that the dialog is a thing people open, close and reopen while
 * deciding, and a list that arrives a beat late every single time reads as the
 * panel being slow. The promise is cached, not the result, so two components
 * mounting at once still make one request.
 */
let cached: Promise<Language[]> | null = null;

function languagesOnce(): Promise<Language[]> {
  if (!cached) {
    cached = fetchLanguages().catch((err) => {
      // A failed fetch must not be remembered as the answer, or the panel is
      // permanently empty until a reload.
      cached = null;
      throw err;
    });
  }
  return cached;
}

/**
 * "KR" → "South Korea", from the browser's own locale data.
 *
 * `Intl.DisplayNames` is in every browser this product supports and costs
 * nothing to ship, which is the whole reason to use it rather than adding a
 * 144-entry table to the bundle that would then need maintaining alongside the
 * registry it describes.
 *
 * It throws a RangeError on anything that is not a region code, and the sprite
 * carries a few marks that are not (`arab`, `asean`), so every lookup is
 * guarded and falls back to the code in capitals. A flag labelled "AW" is worse
 * than one labelled "Aruba" and far better than a panel that does not render.
 */
function countryNamer(): (code: string) => string {
  let dn: Intl.DisplayNames | null = null;
  try {
    dn = new Intl.DisplayNames(['en'], { type: 'region' });
  } catch {
    dn = null;
  }
  return (code: string) => {
    try {
      return dn?.of(code.toUpperCase()) ?? code.toUpperCase();
    } catch {
      return code.toUpperCase();
    }
  };
}

export default function FlagSettings() {
  const { user, flagsOff, setFlagsOff } = useAuth();

  const [languages, setLanguages] = useState<Language[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let alive = true;
    languagesOnce()
      .then((l) => { if (alive) setLanguages(l); })
      .catch(() => { if (alive) setLoadError('The language list could not be loaded.'); });
    return () => { alive = false; };
  }, []);

  const nameOf = useMemo(countryNamer, []);
  const off = useMemo(() => new Set(flagsOff), [flagsOff]);

  /**
   * The rows: one per language that has at least one flag we can actually draw.
   *
   * Languages with no artwork are dropped rather than listed with an empty
   * slot. There is nothing to switch on such a row, and a page of rows that do
   * nothing is how a list of a hundred things stops being read.
   */
  const rows = useMemo(() => {
    if (!languages) return [];
    return languages
      .map((l) => ({ ...l, codes: (l.countries ?? []).filter(hasFlag) }))
      .filter((l) => l.codes.length > 0);
  }, [languages]);

  /**
   * Search covers the language name, its endonym, and the country names and
   * codes on the row — so "korea", "조선", "Korean" and "kr" all land on the
   * same row. Somebody looking for a FLAG is at least as likely to type the
   * country as the language.
   */
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (l) =>
        l.name.toLowerCase().includes(q) ||
        l.nativeName.toLowerCase().includes(q) ||
        l.code.toLowerCase() === q ||
        l.codes.some((c) => c === q || nameOf(c).toLowerCase().includes(q)),
    );
  }, [rows, query, nameOf]);

  /** Every flag that appears anywhere in the list, for the bulk buttons. */
  const everyCode = useMemo(() => {
    const all = new Set<string>();
    for (const row of rows) for (const c of row.codes) all.add(c);
    return [...all];
  }, [rows]);

  const write = useCallback(
    async (next: string[]) => {
      setSaveError(null);
      try {
        await setFlagsOff(next);
      } catch (e) {
        // The provider has already put the old list back, so the switches on
        // screen are the truth again by the time this line runs.
        setSaveError((e as Error).message);
      }
    },
    [setFlagsOff],
  );

  const toggle = useCallback(
    (code: string) => {
      const next = off.has(code) ? flagsOff.filter((c) => c !== code) : [...flagsOff, code];
      void write(next);
    },
    [off, flagsOff, write],
  );

  const hidden = off.size;

  return (
    <div>
      <p className="text-sm text-ink-600 dark:text-ink-300">
        Tap a flag to hide it everywhere it appears.
      </p>

      {/*
        Where the choice is kept, said plainly. A guest's flags live in this
        browser (there is no account to hang them on); an account's follow the
        person to every device. The switches work the same either way — the
        provider writes a guest's list to localStorage and an account's to the
        server. See lib/auth.tsx `setFlagsOff`.
      */}
      {!user && (
        <p className="mt-2 text-xs leading-relaxed text-ink-500 dark:text-ink-400">
          These are saved on this browser.{' '}
          <a href="/register" className="font-medium text-ink-900 underline underline-offset-2 dark:text-white">
            Create a free account
          </a>{' '}
          to keep them on every device.
        </p>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={rows.length ? `Search ${rows.length} languages…` : 'Search…'}
          aria-label="Search languages and countries"
          className={S_INPUT + ' min-w-0 flex-1 basis-56'}
        />
        {/*
          One request, not one per flag — which is the other reason the bulk
          buttons exist. Turning a hundred flags off by hand is a hundred
          writes; this is one.
        */}
        <button
          type="button"
          disabled={hidden === 0}
          onClick={() => { void write([]); }}
          className={S_BTN}
        >
          Show all
        </button>
        <button
          type="button"
          disabled={hidden >= everyCode.length && everyCode.length > 0}
          onClick={() => { void write(everyCode); }}
          className={S_BTN}
        >
          Hide all
        </button>
      </div>

      <p
        className={
          'mt-2 text-[11px] ' +
          (saveError ? 'text-red-600 dark:text-red-400' : 'text-ink-400 dark:text-ink-500')
        }
        role="status"
      >
        {saveError ??
          (hidden === 0
            ? 'Every flag is showing.'
            : `${hidden} flag${hidden === 1 ? '' : 's'} hidden. Saved ${
                user ? 'to your account' : 'on this browser'
              } as you switch them.`)}
      </p>

      <div
        className="mt-3 overflow-hidden rounded-xl border border-ink-200 dark:border-white/10"
      >
        {loadError ? (
          <p className="px-4 py-8 text-center text-sm text-red-600 dark:text-red-400">{loadError}</p>
        ) : !languages ? (
          <p className="px-4 py-8 text-center text-sm text-ink-400 dark:text-ink-500">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-ink-400 dark:text-ink-500">
            No language or country matches “{query}”.
          </p>
        ) : (
          <ul className="divide-y divide-ink-200 dark:divide-white/10">
            {filtered.map((l) => (
              <li key={l.code} className="flex items-center gap-3 px-4 py-2.5">
                <span className="flex min-w-0 grow flex-col">
                  <span className="truncate text-sm font-medium text-ink-900 dark:text-ink-dim">
                    {l.name}
                  </span>
                  <span className="truncate text-[11px] text-ink-400 dark:text-ink-500">
                    {l.codes.map(nameOf).join(' · ')}
                  </span>
                </span>
                <span className="flex shrink-0 items-center gap-1.5">
                  {l.codes.map((c) => (
                    <FlagSwitch
                      key={c}
                      code={c}
                      country={nameOf(c)}
                      on={!off.has(c)}
                      onToggle={() => toggle(c)}
                    />
                  ))}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/**
 * One flag, as a switch.
 *
 * ── Why the artwork stays visible when it is off ────────────────────────────
 * Because this is the one screen where a hidden flag still has to be
 * recognisable: the whole job of the row is "find the flag you want back and
 * press it". Greying it out and striking it through says "not shown" while
 * leaving it identifiable; replacing it with an empty box would make turning
 * one back on a matter of counting positions.
 *
 * `aria-pressed` rather than a checkbox: the control is the flag, and there is
 * no label text to attach a box to. The accessible name spells out both the
 * country and what pressing it does, because "Korea" alone does not say which
 * way this switch is currently thrown.
 */
function FlagSwitch({
  code,
  country,
  on,
  onToggle,
}: {
  code: string;
  country: string;
  on: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      aria-label={on ? `Hide the flag of ${country}` : `Show the flag of ${country}`}
      title={`${country} (${code.toUpperCase()}) — ${on ? 'showing' : 'hidden'}`}
      onClick={onToggle}
      className={
        'relative inline-flex items-center justify-center rounded-md border p-1 transition ' +
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-400 ' +
        (on
          ? 'border-ink-300 bg-white hover:border-ink-500 dark:border-white/20 dark:bg-white/[0.04] dark:hover:border-white/40'
          : 'border-dashed border-ink-200 bg-ink-100 hover:border-ink-400 dark:border-white/10 dark:bg-ink-950')
      }
    >
      <FlagIcon
        code={code}
        className="h-[15px] w-[20px] rounded-[2px] ring-1 ring-black/15 dark:ring-white/20"
        /*
          Desaturated rather than removed — see the note above. Measured on the
          Taegukgi, which is the flag this whole setting exists for and the
          hardest of them to keep legible: at `grayscale(1)` and 0.35 opacity it
          was a grey smudge nobody could have identified, and the row read as
          broken rather than as switched off. A little colour left in it, and
          the strike doing the work of saying “off”, is what keeps it findable.
        */
        style={on ? undefined : { filter: 'grayscale(0.7)', opacity: 0.55 }}
      />
      {!on && (
        /*
          The strike is drawn over the whole button rather than over the flag,
          so it reads at a glance across a column of them. `pointer-events-none`
          because it sits above the button's own hit area.
        */
        <svg
          viewBox="0 0 28 22"
          aria-hidden
          className="pointer-events-none absolute inset-0 h-full w-full text-ink-700
                     dark:text-ink-100"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        >
          {/* A hairline of the button's own background under the stroke, so the
              slash stays visible over a dark flag as well as a pale one. */}
          <path d="M5 17L23 5" className="stroke-white/70 dark:stroke-black/60" strokeWidth="3.4" />
          <path d="M5 17L23 5" />
        </svg>
      )}
    </button>
  );
}
